import { create } from "zustand";
import { persist } from "zustand/middleware";

const STORAGE_KEY = "org-storage";

const INITIAL_STATE = {
  /** All orgs the current user belongs to */
  orgs: [],
  /** Currently-selected org (mirrors primary_organization_id on profile) */
  activeOrgId: null,
  /** Full org detail for the active org (fetched from GET /api/org/:id) */
  activeOrg: null,
  /** Members of the active org */
  members: [],
  isLoading: false,
  error: null,
};

const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3002";

function authHeaders() {
  try {
    const raw = localStorage.getItem("app-session");
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const token =
      parsed?.access_token || parsed?.state?.session?.access_token || null;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
      ...opts.headers,
    },
    ...opts,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(data?.error || `Request failed (${res.status})`);
  }
  return data;
}

export const useOrgStore = create(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      // -------------------------------------------------------------------
      // Core actions
      // -------------------------------------------------------------------

      setActiveOrgId: async (orgId) => {
        set({ activeOrgId: orgId });
        // Persist to server
        try {
          await apiFetch("/api/org/active", {
            method: "PUT",
            body: JSON.stringify({ organization_id: orgId }),
          });
        } catch (e) {
          console.error("Failed to persist active org:", e);
        }
        // Fetch org details if switching to a real org
        if (orgId) {
          await get().fetchOrg(orgId);
          await get().fetchMembers(orgId);
        } else {
          set({ activeOrg: null, members: [] });
        }
      },

      /**
       * Fetch user's org list from entitlements endpoint (orgs array).
       * Called during app initialization after auth completes.
       */
      fetchOrgs: async (entitlements) => {
        if (!entitlements?.orgs) return;
        set({ orgs: entitlements.orgs });
        // If server has an active org, set it locally
        if (
          entitlements.active_org_id &&
          entitlements.orgs.some((o) => o.org_id === entitlements.active_org_id)
        ) {
          const currentActive = get().activeOrgId;
          if (currentActive !== entitlements.active_org_id) {
            set({ activeOrgId: entitlements.active_org_id });
            await get().fetchOrg(entitlements.active_org_id);
          }
        }
      },

      fetchOrg: async (orgId) => {
        try {
          set({ isLoading: true, error: null });
          const data = await apiFetch(`/api/org/${orgId}`);
          set({ activeOrg: data.organization, isLoading: false });
          return data.organization;
        } catch (e) {
          set({ error: e.message, isLoading: false });
          return null;
        }
      },

      fetchMembers: async (orgId) => {
        try {
          const data = await apiFetch(`/api/org/${orgId}/members`);
          set({ members: data.members || [] });
          return data.members;
        } catch (e) {
          console.error("Failed to fetch members:", e);
          return [];
        }
      },

      // -------------------------------------------------------------------
      // Org CRUD
      // -------------------------------------------------------------------

      createOrg: async (name) => {
        set({ isLoading: true, error: null });
        try {
          const data = await apiFetch("/api/org", {
            method: "POST",
            body: JSON.stringify({ name }),
          });
          const org = data.organization;
          set((s) => ({
            orgs: [
              ...s.orgs,
              {
                org_id: org.id,
                org_name: org.name,
                billing_status: org.billing_status,
                effective_plan: "free",
                role: "owner",
              },
            ],
            activeOrgId: org.id,
            activeOrg: org,
            isLoading: false,
          }));
          return org;
        } catch (e) {
          set({ error: e.message, isLoading: false });
          throw e;
        }
      },

      updateOrg: async (orgId, updates) => {
        try {
          const data = await apiFetch(`/api/org/${orgId}`, {
            method: "PATCH",
            body: JSON.stringify(updates),
          });
          set({ activeOrg: data.organization });
          return data.organization;
        } catch (e) {
          set({ error: e.message });
          throw e;
        }
      },

      deleteOrg: async (orgId) => {
        try {
          await apiFetch(`/api/org/${orgId}`, { method: "DELETE" });
          set((s) => ({
            orgs: s.orgs.filter((o) => o.org_id !== orgId),
            activeOrgId: s.activeOrgId === orgId ? null : s.activeOrgId,
            activeOrg: s.activeOrgId === orgId ? null : s.activeOrg,
            members: s.activeOrgId === orgId ? [] : s.members,
          }));
        } catch (e) {
          set({ error: e.message });
          throw e;
        }
      },

      // -------------------------------------------------------------------
      // Members
      // -------------------------------------------------------------------

      inviteMember: async (orgId, email, role = "member") => {
        const data = await apiFetch(`/api/org/${orgId}/members/invite`, {
          method: "POST",
          body: JSON.stringify({ email, role }),
        });
        return data;
      },

      suspendMember: async (orgId, userId) => {
        await apiFetch(`/api/org/${orgId}/members/${userId}/suspend`, {
          method: "POST",
        });
        await get().fetchMembers(orgId);
      },

      changeMemberRole: async (orgId, userId, role) => {
        await apiFetch(`/api/org/${orgId}/members/${userId}/role`, {
          method: "POST",
          body: JSON.stringify({ role }),
        });
        await get().fetchMembers(orgId);
      },

      transferOwnership: async (orgId, newOwnerId) => {
        await apiFetch(`/api/org/${orgId}/members/transfer-ownership`, {
          method: "POST",
          body: JSON.stringify({ new_owner_user_id: newOwnerId }),
        });
        await get().fetchMembers(orgId);
        await get().fetchOrg(orgId);
      },

      // -------------------------------------------------------------------
      // Billing / Seats
      // -------------------------------------------------------------------

      subscribe: async (orgId, seats, billingInterval = "month") => {
        const data = await apiFetch(`/api/org/${orgId}/subscribe`, {
          method: "POST",
          body: JSON.stringify({
            seats,
            billing_interval: billingInterval,
          }),
        });
        return data; // { checkout_url, session_id }
      },

      updateSeats: async (orgId, desiredSeats) => {
        const data = await apiFetch(`/api/org/${orgId}/seats`, {
          method: "PATCH",
          body: JSON.stringify({ desired_seats: desiredSeats }),
        });
        set({ activeOrg: data.organization });
        return data.organization;
      },

      // -------------------------------------------------------------------
      // Invitations
      // -------------------------------------------------------------------

      revokeInvitation: async (orgId, invId) => {
        await apiFetch(`/api/org/${orgId}/invitations/${invId}`, {
          method: "DELETE",
        });
      },

      resendInvitation: async (orgId, invId) => {
        const data = await apiFetch(
          `/api/org/${orgId}/invitations/${invId}/resend`,
          { method: "POST" }
        );
        return data;
      },

      acceptInvitation: async (token) => {
        const data = await apiFetch(`/api/org/accept-invite/${token}`, {
          method: "POST",
        });
        return data;
      },

      // -------------------------------------------------------------------
      // Reset
      // -------------------------------------------------------------------

      reset: () => set(INITIAL_STATE),
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        activeOrgId: state.activeOrgId,
      }),
    }
  )
);
